import PlaylistDetailActions from '../../features/browse/PlaylistDetailActions';
import DetailShell from './PlaylistDetailShell';
import PlaylistExternalLink from './PlaylistExternalLink';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import { DetailReturnContext } from '../review/components/detail/DetailReturnState';
import PlaylistArtwork from '../../components/content/PlaylistArtwork';
import PlaylistAuthor from './PlaylistAuthor';
import Link from '../../features/browse/BrowseLink';
import PlaylistDetailItem, { playlistArtwork } from '../../features/browse/PlaylistDetailContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import {
  recordRecent,
  recentPlaylist,
  recentScope,
  removeRecent,
  removeUnavailableRecent,
} from '../../features/recent/recentContentStore';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentSave } from '../../lib/gaEvents';
import { Typography, Box, Stack } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiEdit2, FiTrash2, FiFlag } from 'react-icons/fi';
import ReviewComments from '../review/components/detail/ReviewComments';
import ReviewSafetyDialog from '../review/components/detail/ReviewSafetyDialog';
import {
  ReviewDraftProvider,
  useReviewDraft,
  useReleaseReviewDrafts,
} from '../review/components/detail/ReviewDraftProtection';
import '../review/UserReviewDetail.css';
import {
  playlistCommentsApi,
  invalidatePlaylistConsumers,
  removeDeletedPlaylist,
} from './playlistActionsModel';
import PlaylistDeleteDialog from './PlaylistDeleteDialog';
import { playlistApi } from '../../api/playlist';
import ProgressBar from '../../components/common/ProgressBar';
import { useUserStore } from '../../stores/userStore';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import { Helmet } from 'react-helmet-async';

import { SITE_URL } from '../../utils/env';
import { previewTrack, webPreviewPlayer } from '../../features/preview/previewPlayer';
import { embedYouTubeInText } from '../../utils/youtube';

const PlaylistDetail = () => {
  const memory = useBrowseSession('playlist-detail');
  const user = useUserStore((s) => s.user),
    { locale } = useWebLocale();
  return (
    <ReviewDraftProvider
      key={`${memory.location.key}:${memory.entry.version}`}
      en={locale === 'en'}
    >
      <DetailReturnContext.Provider value={memory}>
        <PlaylistDetailPage user={user} {...memory} />
      </DetailReturnContext.Provider>
    </ReviewDraftProvider>
  );
};
export const PlaylistDetailPage = ({ user, entry, returning }) => {
  const { slug } = useParams();
  const { locale } = useWebLocale(),
    en = locale === 'en';
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [bookmarkPending, setBookmarkPending] = useState(false);
  const [bookmarkError, setBookmarkError] = useState(false);
  const commentsRef = useRef(null),
    commentInputRef = useRef(null),
    mounted = useRef(true),
    pending = useRef(false);
  const client = useQueryClient(),
    releaseDrafts = useReleaseReviewDrafts();
  const [deleteOpen, setDeleteOpen] = useState(false),
    [deleting, setDeleting] = useState(false),
    [deleteError, setDeleteError] = useState(false);
  const [safety, setSafety] = useState(null),
    [notice, setNotice] = useState(''),
    [liveCommentCount, setLiveCommentCount] = useState(null);
  useReviewDraft(false, bookmarkPending || deleting, en);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const active = () =>
    mounted.current && user && String(useUserStore.getState().user?.id) === String(user.id);
  const assertAccount = () => {
    if (!active()) throw new Error('Inactive account or route');
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const previewTracks = useMemo(
    () =>
      [...(playlist?.tracks || [])]
        .sort((a, b) => a.position - b.position)
        .map((track) => previewTrack(track)),
    [playlist],
  );
  const previewOwner = `playlist:${slug}`;
  useEffect(() => () => webPreviewPlayer.dismissOwned(previewOwner), [previewOwner]);
  const listContentType =
    playlist?.type === 'TRACK' ? CONTENT_TYPE.LIST_SONG : CONTENT_TYPE.LIST_ALBUM;
  useTrackPageView(
    {
      pageType: PAGE_TYPE.LIST_POST,
      contentType: listContentType,
      contentId: playlist?.id,
      contentIdPrefix: 'list',
      contentName: playlist?.title,
    },
    { ready: !!playlist?.id },
  );
  const [isBookmarked, setIsBookmarked] = useState(false);

  // 댓글 스크롤: path parameter에서 commentId 감지
  const commentViewMatch = location.pathname.match(/\/comment\/(\d+)$/);
  const commentScrollId = commentViewMatch ? parseInt(commentViewMatch[1]) : null;
  const detailKey = ['playlist', 'detail', slug, user?.id ?? 'guest'];
  const detailQuery = useQuery({
    queryKey: detailKey,
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({ signal }) => {
      const config = { signal, suppressGlobalError: true, optionalAuth: true };
      const response = /^\d+$/.test(slug)
        ? await playlistApi.getPlaylistById(slug, config)
        : await playlistApi.getPlaylistBySlug(slug, config);
      return response.data;
    },
  });
  // Keep the initial skeleton until the query result has reached local action state.
  const loading =
    !playlist && !detailQuery.isError && (detailQuery.isPending || !!detailQuery.data);
  const loadError = detailQuery.isError;
  const position = useBrowsePosition({
    entry,
    returning,
    ready: Boolean(playlist) && !commentsLoading,
  });
  const initialComment = useRef(returning && entry.y !== undefined ? undefined : commentScrollId);
  const recorded = useRef(false);
  useEffect(() => {
    if (!detailQuery.data) return;
    const data = detailQuery.data;
    // A background identity refresh must not erase an in-flight save or comment draft.
    setPlaylist((previous) =>
      pending.current && previous ? { ...data, bookmarkCount: previous.bookmarkCount } : data,
    );
    if (!pending.current) setIsBookmarked(data.isBookmarked);
    if (!recorded.current) {
      recorded.current = true;
      recordRecent(recentScope(user?.id), recentPlaylist(data));
    }
  }, [detailQuery.data, user?.id]);
  useEffect(() => {
    if (detailQuery.error)
      removeUnavailableRecent(
        recentScope(user?.id),
        `/lists/${encodeURIComponent(slug)}`,
        detailQuery.error,
      );
  }, [detailQuery.error, slug, user?.id]);

  const login = () =>
    navigate(`/login?redirectUrl=${encodeURIComponent(location.pathname + location.search)}`);
  const back = () =>
    window.history.state?.idx > 0 ? navigate(-1) : navigate('/lists', { replace: true });
  const handleBookmarkToggle = async () => {
    if (!user) return login();
    if (!playlist || pending.current || !active()) return;
    pending.current = true;
    void client.cancelQueries({ queryKey: detailKey, exact: true });
    const previous = { saved: isBookmarked, count: playlist.bookmarkCount || 0 };
    setBookmarkPending(true);
    setBookmarkError(false);
    setIsBookmarked(!previous.saved);
    setPlaylist((p) => ({
      ...p,
      bookmarkCount: Math.max(0, previous.count + (previous.saved ? -1 : 1)),
    }));
    try {
      await (previous.saved ? playlistApi.deleteBookmark : playlistApi.addBookmark)(playlist.id, {
        suppressGlobalError: true,
      });
      assertAccount();
      void invalidatePlaylistConsumers(client);
      if (!previous.saved)
        trackContentSave(
          {
            contentType: CONTENT_TYPE.LIST,
            contentId: playlist.id,
            contentName: playlist.title,
            pageType: PAGE_TYPE.LIST_POST,
          },
          user,
        );
    } catch {
      if (active()) {
        setIsBookmarked(previous.saved);
        setPlaylist((p) => ({ ...p, bookmarkCount: previous.count }));
        setBookmarkError(true);
      }
    } finally {
      if (active()) {
        pending.current = false;
        setBookmarkPending(false);
      }
    }
  };
  const isOwner = Boolean(user && playlist && String(user.id) === String(playlist.userId));
  const canDelete = isOwner || user?.role === 'ADMIN';
  const handleDeleteClick = async () => {
    if (!canDelete || !playlist || pending.current || !active()) return;
    pending.current = true;
    setDeleting(true);
    setDeleteError(false);
    try {
      await playlistApi.deletePlaylist(playlist.id, { suppressGlobalError: true });
      assertAccount();
      removeRecent(
        recentScope(user.id),
        (item) => item.id === `playlist:${playlist.id}` || item.id === `playlist:${playlist.slug}`,
      );
      void invalidatePlaylistConsumers(client);
      removeDeletedPlaylist(client, playlist.id);
      releaseDrafts();
      back();
    } catch {
      if (active()) setDeleteError(true);
    } finally {
      if (active()) {
        pending.current = false;
        setDeleting(false);
      }
    }
  };
  const renderPlaylistActions = () => (
    <div className="pp-playlist-owner-actions">
      {isOwner && (
        <button
          disabled={deleting || bookmarkPending}
          onClick={() =>
            navigate(`/lists/${playlist.id}/edit?from=detail`, {
              state: {
                playlistReturn: { id: playlist.id, path: location.pathname + location.search },
              },
            })
          }
        >
          <FiEdit2 size={15} />
          {en ? 'Edit' : '수정'}
        </button>
      )}
      {canDelete ? (
        <button
          className="is-destructive"
          disabled={deleting || bookmarkPending}
          onClick={() => {
            setDeleteError(false);
            setDeleteOpen(true);
          }}
        >
          <FiTrash2 size={15} />
          {deleting ? (en ? 'Deleting…' : '삭제 중…') : en ? 'Delete' : '삭제'}
        </button>
      ) : user && playlist?.username ? (
        <button
          onClick={() =>
            setSafety({
              contentType: 'PLAYLIST',
              contentId: playlist.id,
              reportedUserId: playlist.userId,
              displayName: playlist.username,
            })
          }
        >
          <FiFlag size={14} />
          {en ? 'Report' : '신고'}
        </button>
      ) : null}
    </div>
  );

  return (
    <DetailShell playlist={playlist} position={position}>
      {playlist && (
        <>
          <OpenInAppBanner
            type="playlist"
            title={playlist.title}
            image={playlist.imageUrl || playlist.previewImageUrls?.[0]}
          />
          <SEO
            title={playlist.title}
            description={playlist.description}
            image={playlist.imageUrl}
            url={
              playlist.slug
                ? `${SITE_URL}/lists/${playlist.slug}`
                : `${SITE_URL}/lists/${playlist.id}`
            }
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: '플리', item: `${SITE_URL}/lists` },
                  { '@type': 'ListItem', position: 3, name: playlist.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: playlist.title,
                image: playlist.imageUrl ? [playlist.imageUrl] : undefined,
                datePublished: playlist.createdAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                dateModified: playlist.updatedAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                author: {
                  '@type': 'Person',
                  name: playlist.username,
                  url: `${SITE_URL}/users/${encodeURIComponent(playlist.username)}`,
                },
              })}
            </script>
          </Helmet>
          {(() => {
            const items =
              playlist.type === 'TRACK'
                ? playlist.tracks
                    ?.filter((t) => t.albumImageUrl)
                    .map((t, i) => ({
                      '@type': 'ListItem',
                      name: t.title,
                      image: t.albumImageUrl,
                      url: t.albumSlug
                        ? `${SITE_URL}/reviews/${t.albumSlug}`
                        : t.albumSourceId
                          ? `${SITE_URL}/reviews/${t.albumSource?.toLowerCase()}/${t.albumSourceId}`
                          : `${SITE_URL}/reviews/local/${t.albumId}`,
                      position: i + 1,
                    }))
                : playlist.albums
                    ?.filter((a) => a.albumImageUrl)
                    .map((a, i) => ({
                      '@type': 'ListItem',
                      name: a.title,
                      image: a.albumImageUrl,
                      url: a.slug
                        ? `${SITE_URL}/reviews/${a.slug}`
                        : `${SITE_URL}/reviews/${a.albumSource?.toLowerCase()}/${a.sourceId}`,
                      position: i + 1,
                    }));
            return items?.length > 0 ? (
              <Helmet>
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    itemListElement: items,
                  })}
                </script>
              </Helmet>
            ) : null;
          })()}
        </>
      )}
      <ProgressBar isLoading={loading}>
        {!playlist ? (
          <div role="alert" className="pp-browse-empty">
            <p>
              {en
                ? loadError
                  ? 'Could not load this playlist.'
                  : 'Playlist not found.'
                : loadError
                  ? '플리를 불러오지 못했어요.'
                  : '플리를 찾을 수 없어요.'}
            </p>
            <button className="pp-action" onClick={() => detailQuery.refetch()}>
              {en ? 'Retry' : '다시 시도'}
            </button>
          </div>
        ) : (
          <div className="pp-playlist-detail">
            {loadError && (
              <div role="alert" className="pp-browse-empty">
                <p>
                  {en ? 'Could not refresh this playlist.' : '플리 정보를 새로 불러오지 못했어요.'}
                </p>
                <button className="pp-action" onClick={() => detailQuery.refetch()}>
                  {en ? 'Retry' : '다시 시도'}
                </button>
              </div>
            )}
            <div className="pp-playlist-detail-hero">
              <PlaylistArtwork playlist={playlistArtwork(playlist)} />
              <div className="pp-playlist-detail-copy">
                <h1>{playlist.title}</h1>
                {playlist.username && (
                  <Link to={`/users/${encodeURIComponent(playlist.username)}`}>
                    <PlaylistAuthor playlist={playlist} />
                  </Link>
                )}
                <p>
                  {playlist.type === 'TRACK'
                    ? en
                      ? `${playlist.trackCount || 0} tracks`
                      : `${playlist.trackCount || 0}곡`
                    : en
                      ? `${playlist.albumCount || 0} albums`
                      : `앨범 ${playlist.albumCount || 0}장`}
                  {playlist.createdAt &&
                    ` · ${playlist.createdAt.slice(0, 10).replaceAll('-', '.')}`}
                </p>
              </div>
            </div>

            <Stack spacing={'16px'} sx={{ mt: '16px' }}>
              {/* 본문 */}
              <Box className="pp-playlist-body-content">
                {/* 설명 */}
                {playlist.description && (
                  <Box sx={{ minWidth: 0 }}>
                    {embedYouTubeInText(playlist.description)?.map((part, i) =>
                      part.type === 'youtube' ? (
                        <Box
                          key={i}
                          sx={{
                            position: 'relative',
                            paddingBottom: '56.25%',
                            height: 0,
                            overflow: 'hidden',
                            maxWidth: '100%',
                            my: 1.5,
                            borderRadius: '8px',
                          }}
                        >
                          <iframe
                            src={`https://www.youtube.com/embed/${part.videoId}`}
                            title="YouTube video"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              border: 0,
                            }}
                            allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      ) : (
                        <Typography
                          key={i}
                          fontSize={15}
                          color="var(--pp-review-body, var(--pp-text-primary))"
                          component="span"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: '24px',
                            display: 'block',
                          }}
                        >
                          {part.content}
                        </Typography>
                      ),
                    )}
                  </Box>
                )}
                <div className="pp-playlist-detail-stats">
                  <span>
                    {en
                      ? `Views ${playlist.viewCount || 0} · Comments ${liveCommentCount ?? playlist.commentCount ?? 0} · Saves ${playlist.bookmarkCount || 0}`
                      : `조회 ${playlist.viewCount || 0} · 댓글 ${liveCommentCount ?? playlist.commentCount ?? 0} · 저장 ${playlist.bookmarkCount || 0}`}
                  </span>
                  {renderPlaylistActions()}
                </div>
                <PlaylistDetailActions
                  playlist={playlist}
                  saved={isBookmarked}
                  pending={bookmarkPending || deleting}
                  onSave={handleBookmarkToggle}
                  onComments={() => {
                    commentsRef.current?.scrollIntoView({
                      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                        ? 'auto'
                        : 'smooth',
                      block: 'start',
                    });
                    commentInputRef.current?.focus({ preventScroll: true });
                  }}
                />
                {bookmarkError && (
                  <p role="alert">
                    {en
                      ? 'Could not save. Try again.'
                      : '저장 상태를 변경하지 못했어요. 다시 시도해 주세요.'}
                  </p>
                )}
                <PlaylistExternalLink url={playlist.externalLinkUrl} />
                <div className="pp-playlist-detail-items">
                  {[...(playlist.type === 'TRACK' ? playlist.tracks || [] : playlist.albums || [])]
                    .sort((a, b) => a.position - b.position)
                    .map((item, index) => (
                      <PlaylistDetailItem
                        key={`${item.id}-${index}`}
                        item={item}
                        type={playlist.type}
                        track={previewTracks[index]}
                        queue={previewTracks}
                        owner={previewOwner}
                      />
                    ))}
                  {!(playlist.type === 'TRACK'
                    ? playlist.tracks?.length
                    : playlist.albums?.length) && (
                    <p className="pp-browse-empty">
                      {en ? 'There are no items to show.' : '표시할 항목이 없습니다.'}
                    </p>
                  )}
                </div>
              </Box>

              <div ref={commentsRef} className="pp-playlist-comments">
                <ReviewComments
                  reviewId={playlist.id}
                  en={en}
                  user={user}
                  onLogin={login}
                  onReport={setSafety}
                  assertAccount={assertAccount}
                  onCount={setLiveCommentCount}
                  inputRef={commentInputRef}
                  targetId={initialComment.current}
                  threadApi={playlistCommentsApi}
                  queryNamespace="playlistComments"
                  onLoadingChange={setCommentsLoading}
                />
              </div>
              {notice && <p role="status">{notice}</p>}
              {deleteOpen && (
                <PlaylistDeleteDialog
                  en={en}
                  busy={deleting}
                  error={deleteError}
                  onClose={() => {
                    if (!pending.current) setDeleteOpen(false);
                  }}
                  onDelete={handleDeleteClick}
                />
              )}
              {safety && (
                <ReviewSafetyDialog
                  key={`${safety.contentType}:${safety.contentId}`}
                  target={safety}
                  en={en}
                  assertAccount={assertAccount}
                  onClose={() => setSafety(null)}
                  onNotice={setNotice}
                  onBlocked={() => {
                    void client.invalidateQueries({ queryKey: ['playlistComments'] });
                    void invalidatePlaylistConsumers(client);
                    if (safety.contentType === 'PLAYLIST') {
                      releaseDrafts();
                      back();
                    }
                  }}
                />
              )}
            </Stack>
          </div>
        )}
      </ProgressBar>
    </DetailShell>
  );
};

export default PlaylistDetail;
